import { useCallback } from 'react';
import { Node, useReactFlow } from '@xyflow/react';

import { ForLoopStepUI, StepWithPosition } from '../types/graph';

type UseForLoopDataProps = {
  forLoopNodeId: string;
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
};

export function useForLoopData({ forLoopNodeId, setNodes }: UseForLoopDataProps) {
  const { getNode } = useReactFlow();

  const getForLoopData = useCallback((): ForLoopStepUI | null => {
    try {
      const node = getNode(forLoopNodeId);
      if (!node || node.type !== 'for_loop') return null;

      const data = node.data as ForLoopStepUI;

      // Ensure steps is always an object
      if (!data.steps || typeof data.steps !== 'object') {
        data.steps = {};
      }

      return data;
    } catch (error) {
      console.warn('Error getting ForLoop data:', error);
      return null;
    }
  }, [forLoopNodeId, getNode]);

  const updateForLoopData = useCallback(
    (updates: Partial<ForLoopStepUI>) => {
      setNodes(nodes =>
        nodes.map(node => {
          if (node.id === forLoopNodeId && node.type === 'for_loop') {
            return {
              ...node,
              data: {
                ...node.data,
                ...updates,
              },
            };
          }
          return node;
        })
      );
    },
    [forLoopNodeId, setNodes]
  );

  const addStepToForLoop = useCallback(
    (step: StepWithPosition) => {
      const forLoopData = getForLoopData();
      if (!forLoopData) return;

      const updatedSteps = {
        ...forLoopData.steps,
        [step.step_id]: step,
      };

      const updates: Partial<ForLoopStepUI> = { steps: updatedSteps };

      if (!forLoopData.start_step_id) {
        updates.start_step_id = step.step_id;
      }

      updateForLoopData(updates);
    },
    [getForLoopData, updateForLoopData]
  );

  const removeStepFromForLoop = useCallback(
    (stepId: string) => {
      const forLoopData = getForLoopData();
      if (!forLoopData) return;

      const { [stepId]: _, ...updatedSteps } = forLoopData.steps;

      const updates: Partial<ForLoopStepUI> = { steps: updatedSteps };

      if (forLoopData.start_step_id === stepId) {
        const remainingStepIds = Object.keys(updatedSteps);
        updates.start_step_id = remainingStepIds.length > 0 ? remainingStepIds[0] : null;
      }

      Object.values(updatedSteps).forEach(step => {
        if (step.next_step_id === stepId) {
          step.next_step_id = null;
        }
      });

      updateForLoopData(updates);
    },
    [getForLoopData, updateForLoopData]
  );

  const updateStepInForLoop = useCallback(
    (stepId: string, updates: Partial<StepWithPosition>) => {
      const forLoopData = getForLoopData();
      if (!forLoopData || !forLoopData.steps[stepId]) return;

      const updatedStep = {
        ...forLoopData.steps[stepId],
        ...updates,
      } as StepWithPosition;

      const updatedSteps: Record<string, StepWithPosition> = {
        ...forLoopData.steps,
        [stepId]: updatedStep,
      };

      updateForLoopData({ steps: updatedSteps });
    },
    [getForLoopData, updateForLoopData]
  );

  const setStepConnection = useCallback(
    (sourceStepId: string, targetStepId: string | null) => {
      updateStepInForLoop(sourceStepId, { next_step_id: targetStepId });
    },
    [updateStepInForLoop]
  );

  const setStartStep = useCallback(
    (stepId: string | null) => {
      const forLoopData = getForLoopData();
      if (!forLoopData) return;

      if (stepId && !forLoopData.steps[stepId]) {
        console.warn(`Step ${stepId} does not exist in ForLoop ${forLoopNodeId}`);
        return;
      }

      updateForLoopData({ start_step_id: stepId });
    },
    [forLoopNodeId, getForLoopData, updateForLoopData]
  );

  const getStepsByPosition = useCallback((): StepWithPosition[] => {
    const forLoopData = getForLoopData();
    if (!forLoopData || !forLoopData.steps) return [];

    return Object.values(forLoopData.steps).sort((a, b) => {
      if (!a.position || !b.position) return 0;
      if (a.position.y !== b.position.y) return a.position.y - b.position.y;
      return a.position.x - b.position.x;
    });
  }, [getForLoopData]);

  const validateForLoopData = useCallback((): { isValid: boolean; errors: string[] } => {
    const forLoopData = getForLoopData();
    const errors: string[] = [];

    if (!forLoopData) {
      errors.push('ForLoop data not found');
      return { isValid: false, errors };
    }

    if (!forLoopData.items_source) {
      errors.push('Items source is required');
    }

    if (!forLoopData.item_name) {
      errors.push('Item name is required');
    }

    const stepIds = Object.keys(forLoopData.steps);
    if (stepIds.length === 0) {
      errors.push('At least one step is required in the ForLoop');
    }

    if (forLoopData.start_step_id && !forLoopData.steps[forLoopData.start_step_id]) {
      errors.push('Start step does not exist in the steps collection');
    }

    stepIds.forEach(stepId => {
      const step = forLoopData.steps[stepId];
      if (step.next_step_id && !forLoopData.steps[step.next_step_id]) {
        errors.push(`Step ${stepId} references non-existent next step ${step.next_step_id}`);
      }

      // Validate nested ForLoop steps
      if (step.type === 'for_loop') {
        if (!step.items_source) {
          errors.push(`Nested ForLoop step ${stepId} is missing items_source`);
        }
        if (!step.item_name) {
          errors.push(`Nested ForLoop step ${stepId} is missing item_name`);
        }
        // Recursively validate nested ForLoop steps if they exist
        if (step.steps && Object.keys(step.steps).length > 0) {
          const nestedStepIds = Object.keys(step.steps);
          nestedStepIds.forEach(nestedStepId => {
            const nestedStep = step.steps?.[nestedStepId];
            if (!nestedStep) return;
            if (nestedStep.next_step_id && step.steps && !step.steps[nestedStep.next_step_id]) {
              errors.push(
                `Nested step ${nestedStepId} in ForLoop ${stepId} references non-existent next step ${nestedStep.next_step_id}`
              );
            }
          });
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  }, [getForLoopData]);

  return {
    getForLoopData,
    updateForLoopData,
    addStepToForLoop,
    removeStepFromForLoop,
    updateStepInForLoop,
    setStepConnection,
    setStartStep,
    getStepsByPosition,
    validateForLoopData,
  };
}
